import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";

export enum OttopilotBtlActivityTemplates {
    doorToDoor = "573d1c34-5324-4f6f-9fb7-87faffe3a422",
    leadCaptureBooth = "5100b875-1837-4697-a3ec-54bf1da081e9",
    roadshow = "39464ada-e4a4-4b5d-89ef-aac4f6872edb",
    referralDriveCampaign = "8268ef89-bde7-418b-b1d7-d7acea459bf7",
    dealerFootfallBooster = "9cb987f0-d21d-4554-b62b-34b4da47e631",
}

export enum OttopilotBtlAssignmentMode {
    auto = "93c004a3-3690-4981-abb1-186da5d82e47",
    manual = "d99c1dc7-8744-4a3e-82fd-ed8b869042b4",
}

export enum OttopilotBtlActivityStatus {
    scheduled = "49268234-2c3b-4456-baee-e2d8fc2fa437",
    cancelled = "c55c6e6d-7400-436f-914a-910f7caacd03",
    completed = "b9f36da7-f8ae-4c6b-b84d-9f2b1448cdc2",
    draft = "37219b57-42f9-4678-81d2-2c24e5102c39",
    active = "b138777b-5564-4046-9abe-c42bb6fc6730",
}

export enum OttopilotBtlReviewStatus {
    valid = "valid",
    invalid = "invalid",
    warning = "warning",
}

export type OttopilotBtlActivityGalleryItem = {
    assetId: string;
    fileName: string;
    uploadedAt: Iso8601DateTime;
};

export type OttopilotBtlChecklistMediaItem = {
    assetId: Uuid;
    fileName: string;
    uploadedAt: Iso8601DateTime;
};

export type OttopilotBtlChecklistResponseEntry = {
    id: Uuid;
    /** UUID of the user who posted this response */
    userId: Uuid;
    /** Timestamp the response was created */
    createdAt: Iso8601DateTime;
    /** Free-text comment / observation */
    textInput: string;
    /** Uploaded media assets (photos, docs, etc.) */
    media: Array<OttopilotBtlChecklistMediaItem>;
};

/**
 * Full map of checklist responses for a BTL activity.
 * Keyed by `{checklistId}:{itemId}` - a composite key that
 * uniquely identifies each task within the activity's template.
 */
export type OttopilotBtlActivityChecklistResponses = Array<{
    checklistId: Uuid;
    checklistItemId: Uuid;
    responses: Array<OttopilotBtlChecklistResponseEntry>;
}>;

export type OttopilotBtlActivity = {
    id: Uuid;
    btlActivityTemplateId: Uuid;
    createdBy: Uuid;
    createdAt: Iso8601DateTime;
    updatedBy: Uuid;
    updatedAt: Iso8601DateTime;
    btlStatus: OttopilotBtlActivityStatus;
    title: string;
    description: string;
    timezone: Uuid;
    startDate: Iso8601DateTime;
    endDate: Iso8601DateTime;
    isAlldayEvent: boolean;
    assignmentMode: OttopilotBtlAssignmentMode;
    assignedUsers: Array<{
        userTemplateId: Uuid;
        userIds: Array<Uuid>;
    }>;
    executionRole: Uuid | null;
    geographyNodes: Array<{
        ouTemplateId: Uuid;
        ouIds: Array<Uuid>;
    }>;
    gallery: Array<OttopilotBtlActivityGalleryItem>;
    /** Indexed checklist task responses for this event. */
    checklistResponses: OttopilotBtlActivityChecklistResponses;
};
